import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageLayout } from '../components/layout';
import { Loader2, RefreshCw, X, Users, FileText, MessageSquare, Share2, User, Tag, Video, Image, Plus, Sparkles, Info, Save, CheckCircle, Brain, ImageOff, Link as LinkIcon, Check } from 'lucide-react';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { ContentToneSelect } from '../components/common/ContentToneSelect';
import { ContentGoalSelect } from '../components/common/ContentGoalSelect';
import { LanguageInput } from '../components/common/LanguageInput';
import { LanguageDropdown } from '../components/common/LanguageDropdown';
import { SocialMediaDropdown } from '../components/common/SocialMediaDropdown';
import { TagInput } from '../components/common/TagInput';
import { ContentResultCard } from '../components/content/ContentResultCard';
import { DatabaseErrorBoundary } from '../components/common/DatabaseErrorBoundary';
import { useBrands } from '../hooks/useBrands';
import { usePersonas } from '../hooks/usePersonas';
import { useContentGeneration } from '../hooks/useContentGeneration';
import { useContentIdeasWithAccumulation } from '../hooks/useContentIdeas';
import { useQueryClient } from '@tanstack/react-query';
import { CONTENT_LENGTH_OPTIONS, SOCIAL_PLATFORMS, API_BASE_URL } from '../config/constants';
import { ContentGenerationRequest, ContentGenerationResponse, PlatformContent } from '../services/contentService';
import { auth } from '../config/firebase';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { SocialIcon } from '../components/icons/SocialIcon';
import { Link } from 'react-router-dom';
import { ImageStyleSelect } from '../components/common/ImageStyleSelect';
import { ContentGenerationLoader } from '../components/content/ContentGenerationLoader';
import { ImageSizeSelect } from '../components/common/ImageSizeSelect';
import { PersonasResponse } from '../types/personas';
import { saveDraft } from '../services/draftService';
import { AIPostDraftModal } from '../components/modals/AIPostDraftModal';
import { AITextCompareModal } from '../components/modals/AITextCompareModal';
import { useImageRegeneration } from '../hooks/useImageRegeneration';
import { 
  convertImageSizeToAspectRatio, 
  convertImageStyleToStyleType 
} from '../services/imageService';
import type { ImageSize } from '../components/common/ImageSizeSelect';
import { SocialProfilePicture } from '../components/common/SocialProfilePicture';
import { WaitingModal } from '../components/common/WaitingModal';
import { AISimpleGenerationModal } from '../components/modals/AISimpleGenerationModal';
import { SpeechToTextButton } from '../components/common/SpeechToTextButton';
import { LottieEqualizer } from '../components/common/LottieEqualizer';
import { logger } from '../utils/logger';
import { withIndexedDBErrorHandling, handleIndexedDBError } from '../utils/indexedDbErrorHandler';
import { imageUrlToFile } from '../utils/profileImageUpload';

// Define content idea interface to match the API response
interface ContentIdea {
  title: string;
  description: string;
  platforms: string[];
  valueProposition: string;
  contentFormat: string;
  goal?: string;
}

// Define platform type for SocialMediaDropdown
interface SocialMediaPlatform {
  id: SocialPlatform | 'all';
  name: string;
}

// Update ContentGenerationRequest to include imageStyle
interface ContentGenerationRequestWithStyle extends ContentGenerationRequest {
  imageStyle?: string;
  imageSize?: string;
  style_type?: string;
  aspect_ratio?: string;
  useExtractedImage?: boolean;
}

// Memoize the ImageStyleSelect component to prevent unnecessary rerenders
const MemoizedImageStyleSelect = React.memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => {
  // Force refresh on language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language]);
  
  return (
    <ImageStyleSelect 
      key={`image-style-${key}-${i18n.language}`} 
      value={value} 
      onChange={onChange} 
    />
  );
});

// Memoize the ContentToneSelect component to prevent unnecessary rerenders
const MemoizedContentToneSelect = React.memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => {
  // Force refresh on language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language]);
  
  return (
    <ContentToneSelect 
      key={`tone-select-${key}-${i18n.language}`} 
      value={value} 
      onChange={onChange} 
    />
  );
});

// Memoize the ImageSizeSelect component to prevent unnecessary rerenders
const MemoizedImageSizeSelect = React.memo(({ value, onChange }: { value: any, onChange: (value: any) => void }) => {
  // Force refresh on language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language]);
  
  return (
    <ImageSizeSelect 
      key={`image-size-${key}-${i18n.language}`} 
      value={value} 
      onChange={onChange} 
    />
  );
});

// Memoize the LanguageDropdown component to force refresh when language changes
const MemoizedLanguageDropdown = React.memo(({ value, onChange, buttonLabel }: { 
  value: string, 
  onChange: (value: string) => void,
  buttonLabel: string 
}) => {
  // Force refresh on both app language change and form language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language, value]); // Include 'value' to trigger refresh when form language changes
  
  return (
    <LanguageDropdown 
      key={`language-dropdown-${key}-${i18n.language}-${value}`} 
      value={value}
      onChange={onChange}
      buttonLabel={buttonLabel}
    />
  );
});

// Description textarea component (removed memo to ensure updates work correctly)
const MemoizedDescriptionTextarea = ({ 
  value, 
  onChange, 
  placeholder 
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  
  return (
    <textarea
      id="shortDescription"
      name="shortDescription"
      rows={6}
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm pt-2 pb-8 pl-3 pr-20 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
    />
  );
};

const ContentCreation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  // Ref to control when content generation is allowed
  const allowContentGeneration = React.useRef(false);
  // Ref to track if the description field has been manually edited
  const manuallyEditedDescription = React.useRef(false);
  // Ref to track if we've already done an automatic content idea selection
  const hasAutoSelectedIdea = React.useRef(false);
  
  // New state for AIPostDraftModal
  const [isAIPostDraftModalOpen, setIsAIPostDraftModalOpen] = useState(false);
  const [selectedPostForEditing, setSelectedPostForEditing] = useState<{
    platform: string;
    content: string;
    hashtags?: string[];
    imageUrl?: string | null;
  } | null>(null);
  const [isSavingDrafts, setIsSavingDrafts] = useState(false);
  // Regenerate menu state
  const [isRegenerateMenuOpen, setIsRegenerateMenuOpen] = useState(false);
  const regenerateMenuRef = useRef<HTMLDivElement | null>(null);
  // Track last generated image style/size to preset the modal with the style used for the current image
  const [lastGeneratedImageStyle, setLastGeneratedImageStyle] = useState<string | null>(null);
  const [lastGeneratedImageSize, setLastGeneratedImageSize] = useState<ImageSize | null>(null);
  
  // Image regeneration modal state
  const [isImageRegenerationModalOpen, setIsImageRegenerationModalOpen] = useState(false);
  const [temporaryGeneratedImageUrl, setTemporaryGeneratedImageUrl] = useState<string | null>(null);
  
  // Keywords input state
  const [keywordsInputValue, setKeywordsInputValue] = useState('');
  
  // State to track when language was set from brand
  const [languageSetFromBrand, setLanguageSetFromBrand] = useState(false);
  
  // State to track when imageStyle was set from brand
  const [imageStyleSetFromBrand, setImageStyleSetFromBrand] = useState(false);
  
  // Image regeneration hook
  const imageRegeneration = useImageRegeneration();
  
  useEffect(() => {
    document.title = t('contentCreation.title', 'Content Creation');
  }, [t]);
  
  const [formData, setFormData] = useState<ContentGenerationRequestWithStyle>({
    brandId: '',
    personaId: '',
    platforms: [] as string[],
    topic: '',
    shortDescription: '',
    goal: '',
    tone: 'professional',
    contentLength: 'medium',
    language: (() => {
      const rawLanguage = (typeof i18n.language === 'string' ? i18n.language : 'en') as string;
      const availableLanguages = [
        'en', 'en-US', 'en-GB', 'fr', 'fr-CA', 'de', 'es', 'es-MX', 
        'it', 'pt', 'pt-BR', 'nl', 'ru', 'ar', 'zh', 'ja', 'ko', 'hi', 'bn', 
        'tr', 'vi', 'tl', 'fa', 'pl', 'th', 'sv', 'ro', 'uk', 'cs', 'lb', 'da', 'fi', 'no'
      ];
      
      // Convert to lowercase for case-insensitive matching
      const normalizedLanguage = rawLanguage.toLowerCase();
      
      // Try exact match first, then language-only fallback
      if (availableLanguages.includes(normalizedLanguage)) {
        return normalizedLanguage;
      }
      const languageOnly = normalizedLanguage.split('-')[0];
      return availableLanguages.includes(languageOnly) ? languageOnly : 'en';
    })(),
    keywords: [] as string[],
    sourceUrl: '',
    format: 'regular',
    generateImage: false,
    imageStyle: 'auto',
    imageSize: '4:5',
    useExtractedImage: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedContent, setGeneratedContent] = useState<ContentGenerationResponse | null>(null);
  const [allCopied, setAllCopied] = useState(false);
  const [hasGeneratedNewContent, setHasGeneratedNewContent] = useState(false);
  // UI-only: listening flag to drive the ChatGPT-like equalizer under textarea
  const [isListening, setIsListening] = useState(false);
  const [listeningStartTs, setListeningStartTs] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const listeningTimerRef = useRef<number | null>(null);
  
  // Article summary states
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [articlePreview, setArticlePreview] = useState<{
    image?: string;
    recap?: string;
    title?: string;
    description?: string;
  } | null>(null);

  // Format seconds to mm:ss
  const formatElapsed = (totalSeconds: number): string => {
    const m = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(1, '0');
    const s = Math.floor(totalSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

  const getLanguageFlag = (lang: string): string => {
    const languageFlags: Record<string, string> = {
      'fr-FR': '🇫🇷', 'fr-CA': '🇨🇦', 'fr': '🇫🇷',
      'en-US': '🇺🇸', 'en-GB': '🇬🇧', 'en': '🇺🇸',
      'es-ES': '🇪🇸', 'es-MX': '🇲🇽', 'es': '🇪🇸',
      'de-DE': '🇩🇪', 'de': '🇩🇪', 'it-IT': '🇮🇹', 'it': '🇮🇹',
      'pt-BR': '🇧🇷', 'pt-PT': '🇵🇹', 'pt': '🇧🇷',
      'nl-NL': '🇳🇱', 'nl': '🇳🇱', 'ru-RU': '🇷🇺', 'ru': '🇷🇺',
      'ar-SA': '🇸🇦', 'ar': '🇸🇦', 'zh-CN': '🇨🇳', 'zh': '🇨🇳',
      'ja-JP': '🇯🇵', 'ja': '🇯🇵', 'ko-KR': '🇰🇷', 'ko': '🇰🇷'
    };
    return languageFlags[lang] || `🌐`;
  };
  // Compare text modal state
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [compareItems, setCompareItems] = useState<Array<{ platform: string; oldContent: string; newContent: string }>>([]);
  const [pendingNewTextByPlatform, setPendingNewTextByPlatform] = useState<Record<string, string>>({});

    // Get current language
  const currentLanguage = i18n.language || 'en';

  // Convert social platforms to the format expected by SocialMediaDropdown
  // Exclude TikTok and YouTube for content generation (temporarily)
  const socialPlatforms: SocialMediaPlatform[] = SOCIAL_PLATFORMS
    .filter(platform => !['tiktok', 'youtube'].includes(platform.value))
    .map(platform => ({
      id: platform.value as SocialPlatform,
      name: platform.label
    }));

  // Fetch brands and personas with error handling
  const { data: brandsData, error: brandsError } = useBrands();
  const brands = brandsData ? (brandsData as any).brands || [] : [];

  // Handle brands data loading errors
  useEffect(() => {
    if (brandsError) {
      handleIndexedDBError(brandsError, 'brands-fetch');
    }
  }, [brandsError]);

  // Get connected social accounts for the selected brand
  const getConnectedSocialAccounts = () => {
    if (!formData.brandId || !brands.length) return [];
    
    const selectedBrand = brands.find((b: any) => b.id === formData.brandId);
    if (!selectedBrand?.social_accounts) return [];
    
    return selectedBrand.social_accounts
      .filter((account: any) => account.platform && account.username)
      .map((account: any) => ({
        platform: account.platform.toLowerCase() as SocialPlatform,
        username: account.username,
        picture_url: account.profile_url || account.picture_url || ''
      }));
  };

  const connectedAccounts = getConnectedSocialAccounts();
  


  // Auto-select brand if there's only one
  useEffect(() => {
    if (brands && brands.length === 1 && !formData.brandId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Auto-selecting single brand', brands[0].id);
      }
      // console.log('Auto-selecting single brand:', brands[0].id);
      setFormData(prev => ({ ...prev, brandId: brands[0].id }));
    }
  }, [brands, formData.brandId]);

  // Handle navigation from BrandCard or PersonaCard
  useEffect(() => {
    const state = location.state as any;
    if (state) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Received navigation state', state);
      }

      const { 
        selectedBrandId, 
        selectedPersonaId, 
        createManually, 
        returnToBrands, 
        returnToPersonas, 
        recommendedPlatforms,
        preFilledTopic,
        preFilledDescription,
        fromBoostMode
      } = state;
      
      // Set brand if provided
      if (selectedBrandId && selectedBrandId !== formData.brandId) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Setting brand from navigation', selectedBrandId);
        }
        // console.log('Setting brand from navigation:', selectedBrandId);
        setFormData(prev => ({ ...prev, brandId: selectedBrandId }));
      }
      
      // Set persona if provided (will be set after personas are loaded)
      if (selectedPersonaId && selectedPersonaId !== formData.personaId) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Will set persona from navigation', selectedPersonaId);
        }
        console.log('Will set persona from navigation:', selectedPersonaId);
        // We'll set this in a separate effect after personas are loaded
      }
      
      // Set recommended platforms if provided from PersonaCard
      if (recommendedPlatforms && Array.isArray(recommendedPlatforms) && recommendedPlatforms.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Setting recommended platforms', recommendedPlatforms);
        }
        console.log('ContentCreation: Setting recommended platforms from persona:', recommendedPlatforms);
        // Normalize platform names to match our expected format
        // Exclude TikTok and YouTube for content generation (temporarily)
        const availablePlatformIds = SOCIAL_PLATFORMS
          .filter(platform => !['tiktok', 'youtube'].includes(platform.value))
          .map(p => p.value);
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Available platform IDs', availablePlatformIds);
        }
        console.log('ContentCreation: Available platform IDs:', availablePlatformIds);
        
        const normalizedPlatforms = recommendedPlatforms
          .map(platform => platform.toLowerCase())
          .filter(platform => availablePlatformIds.includes(platform));
        
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Normalized platforms', normalizedPlatforms);
          logger.debug('ContentCreation', 'Current platforms before update', formData.platforms);
        }
        console.log('ContentCreation: Normalized platforms to set:', normalizedPlatforms);
        
        if (normalizedPlatforms.length > 0) {
          setFormData(prev => {
            if (process.env.NODE_ENV === 'development') {
              logger.debug('ContentCreation', 'Updating platforms', { from: prev.platforms, to: normalizedPlatforms });
            }
            console.log('ContentCreation: Updating platforms from', prev.platforms, 'to', normalizedPlatforms);
            return { ...prev, platforms: normalizedPlatforms };
          });
        } else {
          if (process.env.NODE_ENV === 'development') {
            logger.debug('ContentCreation', 'No valid platforms to set');
          }
          console.log('ContentCreation: No valid platforms found to set');
        }
      }
      
      // Handle pre-filled data from BoostMode
      if (fromBoostMode) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Handling data from BoostMode', { preFilledTopic, preFilledDescription });
        }
        console.log('ContentCreation: Handling data from BoostMode');
        
        // Pre-fill topic and description if provided
        if (preFilledTopic || preFilledDescription) {
          setFormData(prev => ({
            ...prev,
            topic: preFilledTopic || prev.topic,
            shortDescription: preFilledDescription || prev.shortDescription
          }));
          
          // Mark description as manually edited to prevent auto-selection from overwriting it
          if (preFilledDescription) {
            manuallyEditedDescription.current = true;
          }
          
          if (process.env.NODE_ENV === 'development') {
            logger.debug('ContentCreation', 'Pre-filled form with BoostMode data', { 
              topic: preFilledTopic, 
              description: preFilledDescription 
            });
          }
          console.log('ContentCreation: Pre-filled form with:', { 
            topic: preFilledTopic, 
            description: preFilledDescription 
          });
        }
      }
      
      // Clear the state after handling to prevent re-processing
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  // Only use mock data if explicitly debugging, otherwise use real API data
  // Load ALL personas once and filter client-side (same approach as Personas.tsx)
  const { data: personasData, isLoading: isLoadingPersonas, error: personasError } = usePersonas(
    undefined, // No server-side filtering by brand - we'll filter client-side  
    false, 
    false
  );
  const allPersonas = (personasData as PersonasResponse)?.personas || [];

  // Handle personas data loading errors
  useEffect(() => {
    if (personasError) {
      handleIndexedDBError(personasError, 'personas-fetch');
    }
  }, [personasError]);
  
  // Filter personas by selected brand (client-side filtering)
  const personas = useMemo(() => {
    if (!allPersonas.length) return [];
    
    // If no brand is selected, show all personas (default behavior)
    if (!formData.brandId) return allPersonas;
    
    // If a brand is selected, filter by that brand
    return allPersonas.filter(persona => persona.brandId === formData.brandId);
  }, [allPersonas, formData.brandId]);

  // Normalize language to 2-letter code (e.g., "fr-FR" -> "fr", "en-US" -> "en")
  const normalizeLanguage = useCallback((lang: string | undefined | null): string => {
    if (!lang) return 'en';
    // Extract first 2 characters and convert to lowercase
    const normalized = lang.substring(0, 2).toLowerCase();
    return normalized || 'en';
  }, []);

  // For content IDEAS: Use ONLY the application language (i18n.language)
  // This is different from content generation which uses persona/form language
  const appLanguage = useMemo(() => {
    const normalized = normalizeLanguage(i18n.language);
    console.log('🌍 APP LANGUAGE for content ideas:', normalized, 'from i18n.language:', i18n.language);
    return normalized;
  }, [i18n.language, normalizeLanguage]);

  // Fetch content ideas with accumulation and exclusion logic
  // State to track if ideas should be loaded (only when user clicks "Generate ideas")
  const [shouldLoadIdeas, setShouldLoadIdeas] = useState(false);
  
  const {
    ideas: contentIdeas,
    isLoading: isIdeasLoading, 
    isGeneratingMore: isGeneratingMoreIdeas,
    error: ideasError,
    generateMoreIdeas,
    refetch: refetchIdeas
  } = useContentIdeasWithAccumulation({
    brandId: shouldLoadIdeas ? (formData.brandId || '') : '',
    personaId: shouldLoadIdeas ? (formData.personaId || '') : '',
    appLanguage: appLanguage
  });
  
  // Handle initial "Generate ideas" click
  const handleGenerateIdeas = async () => {
    if (!formData.brandId || !formData.personaId) {
      return;
    }
    
    // DEBUG: Force log the language being used
    console.log('='.repeat(80));
    console.log('🔥 GENERATING IDEAS - LANGUAGE DEBUG:');
    console.log('i18n.language (raw):', i18n.language);
    console.log('appLanguage (normalized, sent to API):', appLanguage);
    console.log('='.repeat(80));
    
    if (!shouldLoadIdeas) {
      // First time clicking - enable loading and trigger fetch
      setShouldLoadIdeas(true);
      // Wait a bit for the hook to enable, then refetch
      setTimeout(() => {
        refetchIdeas();
      }, 100);
    } else {
      // Already loaded - generate more ideas
      await handleGenerateMoreIdeas();
    }
  };

  // Handle content ideas errors
  useEffect(() => {
    if (ideasError) {
      handleIndexedDBError(ideasError, 'content-ideas-fetch');
    }
  }, [ideasError]);

  // Handle error messages
  const ideasErrorMessage = (ideasError as Error)?.message;
  const hasIdeasError = Boolean(ideasError);
  
  // Reset shouldLoadIdeas when brand or persona changes
  useEffect(() => {
    setShouldLoadIdeas(false);
  }, [formData.brandId, formData.personaId]);

  // Invalidate content ideas cache when language changes
  useEffect(() => {
    if (formData.brandId && formData.personaId) {
      queryClient.invalidateQueries({ 
        queryKey: ['contentIdeas', formData.brandId, formData.personaId] 
      });
    }
  }, [appLanguage, formData.brandId, formData.personaId, queryClient]);
  
  // Reset auto-selection flag when brand or persona changes
  useEffect(() => {
    if (formData.brandId && formData.personaId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Brand/Persona changed', `${formData.brandId}/${formData.personaId}`);
      }
      console.log(`🔄 Brand/Persona changed: ${formData.brandId}/${formData.personaId} - resetting auto-selection flag`);
      // Reset auto-selection flag when brand/persona changes to allow new auto-selection
      hasAutoSelectedIdea.current = false;
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Auto-selection flag reset');
      }
      console.log('✅ Auto-selection flag reset, new ideas will be auto-selected when they load');
    }
    
    // Reset language and imageStyle set from brand flags when brand changes
    setLanguageSetFromBrand(false);
    setImageStyleSetFromBrand(false);
  }, [formData.brandId, formData.personaId]);

  // Auto-select a random content idea if user hasn't filled Source URL or Description (only once)
  useEffect(() => {
    // Only proceed if we have ideas, no manual input from user, and haven't auto-selected before
    if (contentIdeas.length > 0 && 
        !formData.sourceUrl?.trim() && 
        !formData.shortDescription?.trim() &&
        !manuallyEditedDescription.current &&
        !hasAutoSelectedIdea.current) {
      
      // Select a random idea
      const randomIndex = Math.floor(Math.random() * contentIdeas.length);
      const randomIdea = contentIdeas[randomIndex];
      
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Auto-selecting random content idea', randomIdea);
      }
      console.log('Auto-selecting random content idea (first time only):', randomIdea);
      
      // Mark that we've done an auto-selection
      hasAutoSelectedIdea.current = true;
      
      // Use the existing handleIdeaSelect function to maintain consistency
      handleIdeaSelect(randomIdea);
    }
  }, [contentIdeas, formData.sourceUrl, formData.shortDescription]);
  
  // Handle persona selection from navigation after personas are loaded
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedPersonaId && personas.length > 0) {
      const targetPersona = personas.find(p => p.id === state.selectedPersonaId);
      if (targetPersona && targetPersona.id !== formData.personaId) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Setting persona from navigation', targetPersona.id);
        }
        console.log('Setting persona from navigation:', targetPersona.id);
        setFormData(prev => ({ ...prev, personaId: targetPersona.id }));
      }
    }
  }, [personas, location.state, formData.personaId]);

  
  // Log for debugging
  useEffect(() => {
    if (formData.brandId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Current brand ID', formData.brandId);
        logger.debug('ContentCreation', 'Personas loaded', personas);
      }
      // Removed verbose console logs for current brand and personas
    }
  }, [formData.brandId, personas]);



  // Auto-select persona when brand changes
  useEffect(() => {
    if (formData.brandId && personas.length > 0) {
      // 1. Est-ce qu'il y a un seul persona ?
      if (personas.length === 1) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Auto-selecting single persona', personas[0].id);
        }
        // 2. Oui, alors on le sélectionne automatiquement
        console.log('Auto-selecting single persona:', personas[0].id);
        setFormData(prev => ({ ...prev, personaId: personas[0].id }));
      } else {
        // Non? On attend que l'utilisateur le fasse - on reset le persona si plusieurs disponibles
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Multiple personas available, waiting for user selection');
        }
        console.log('Multiple personas available, user needs to select');
        if (formData.personaId) {
          // Vérifier si le persona actuel est encore valide pour cette marque
          const isCurrentPersonaValid = personas.some(p => p.id === formData.personaId);
          if (!isCurrentPersonaValid) {
            setFormData(prev => ({ ...prev, personaId: '' }));
          }
        }
      }
    } else if (formData.brandId && personas.length === 0) {
      // Pas encore de personas chargés ou aucun persona pour cette marque
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'No personas available for this brand');
      }
      console.log('No personas available for this brand yet');
      setFormData(prev => ({ ...prev, personaId: '' }));
    }
  }, [formData.brandId, personas]);

  // Reset platforms when brand changes (no auto-selection)
  // But don't reset if platforms were just set from navigation
  useEffect(() => {
    if (formData.brandId && brands.length > 0 && !location.state) {
      // Only reset platforms if we're not coming from navigation
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Brand changed, resetting platform selection');
      }
      console.log('Brand changed, resetting platform selection');
      setFormData(prev => ({ ...prev, platforms: [] }));
    }
  }, [formData.brandId, brands, location.state]);

  // Content generation mutation
  const contentGeneration = useContentGeneration();

  // Create a ref for the results section
  const resultsRef = useRef<HTMLDivElement>(null);

  // Function to scroll to the top of the page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to scroll to the results section
  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Function to transform the new API response format to the format expected by the UI
  const transformContentResponse = (response: ContentGenerationResponse) => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('ContentCreation', 'Transforming content response', response);
    }
    console.log('Transforming content response:', response);
    
    // Handle completely empty responses
    if (!response) {
      console.error('Content response is empty or undefined');
      return {
        success: false,
        message: 'Empty response received',
        data: { contents: [] }
      };
    }
    
    // Extract imageUrl from the response (main image URL at the top level)
    const imageUrl = response.imageUrl;
    
    // Check if we have the new API format with generatedContents array
    if (response.generatedContents && Array.isArray(response.generatedContents)) {
      try {
        console.log('Processing response with generatedContents array');
        
        const transformedContents = response.generatedContents.map(item => {
          // Process content to convert escaped newlines to real ones
          let processedContent = item.generatedContent || '';
          if (typeof processedContent === 'string' && processedContent.includes('\\n')) {
            processedContent = processedContent.replace(/\\n/g, '\n');
          }
          
          return {
            platform: item.platform?.toLowerCase() || 'unknown',
            content: processedContent,
            hashtags: extractHashtags(processedContent),
            // Use platform-specific image if available, otherwise fall back to main image
            imageUrl: item.imageUrl || response.imageUrl
          };
        });
        
        console.log('Transformed content from generatedContents:', transformedContents);
        
        return {
          ...response,
          success: true,
          data: { contents: transformedContents }
        };
      } catch (error) {
        console.error('Error transforming generatedContents:', error);
        // Continue to fallback handler
      }
    }
    
    // Try to extract any content we can find in the response
    let fallbackContents: Array<{platform: string; content: string | PlatformContent; hashtags?: string[]}> = [];
    
    try {
      if (response.generatedContents && Array.isArray(response.generatedContents)) {
        fallbackContents = response.generatedContents.map(content => {
          // Process content to convert escaped newlines to real ones
          let processedContent = content.generatedContent || '';
          if (typeof processedContent === 'string' && processedContent.includes('\\n')) {
            processedContent = processedContent.replace(/\\n/g, '\n');
          }
          
          return {
            platform: content.platform?.toLowerCase() || 'unknown',
            content: processedContent,
            hashtags: extractHashtags(processedContent)
          };
        });
        console.log('Extracted from generatedContents array:', fallbackContents);
      } else if (response.data?.contents) {
        // Process any string content to fix escaped newlines
        fallbackContents = response.data.contents.map(item => {
          if (typeof item.content === 'string' && item.content.includes('\\n')) {
            return {
              ...item,
              content: item.content.replace(/\\n/g, '\n')
            };
          }
          return item;
        });
        console.log('Using existing contents array:', fallbackContents);
      } else if (response.content?.platforms) {
        // Handle the 'content.platforms' object format
        fallbackContents = Object.entries(response.content.platforms).map(([platform, content]) => {
          // Handle string content with escaped newlines
          if (typeof content === 'string') {
            const stringContent = content as string;
            if (stringContent.includes('\\n')) {
              return {
                platform: platform.toLowerCase(),
                content: stringContent.replace(/\\n/g, '\n'),
                hashtags: extractHashtags(stringContent.replace(/\\n/g, '\n'))
              };
            }
            return {
              platform: platform.toLowerCase(),
              content: stringContent,
              hashtags: extractHashtags(stringContent)
            };
          } else if (content && typeof content === 'object') {
            const objContent = content as Record<string, any>;
            if ('text' in objContent && typeof objContent.text === 'string' && objContent.text.includes('\\n')) {
              const processedContent = {
                ...objContent,
                text: objContent.text.replace(/\\n/g, '\n')
              };
              return {
                platform: platform.toLowerCase(),
                content: processedContent,
                hashtags: extractHashtags(processedContent)
              };
            }
          }
          
          // Default case - return as is
          return {
            platform: platform.toLowerCase(),
            content: content,
            hashtags: extractHashtags(content)
          };
        });
        console.log('Extracted from content.platforms object:', fallbackContents);
      }
    } catch (error) {
      console.error('Error extracting content from response:', error);
    }
    
    return {
      ...response,
      success: response.success === undefined ? true : response.success, // Preserve success value if it exists
      message: response.message || 'Content format not recognized',
      data: { 
        contents: fallbackContents.length > 0 ? fallbackContents : []
      }
    };
  };
  
  // Helper function to extract hashtags from content
  const extractHashtags = (content: string | PlatformContent | any) => {
    const hashtags: string[] = [];
    
    // Ensure we have a string to extract hashtags from
    let contentString = '';
    
    if (typeof content === 'string') {
      // Preserve the original string with newlines
      contentString = content;
    } else if (content && typeof content === 'object') {
      // Check if it has a text property (PlatformContent)
      if ('text' in content && content.text) {
        contentString = typeof content.text === 'string' 
          ? content.text 
          : JSON.stringify(content.text);
      } else {
        // Otherwise stringify the whole object
        contentString = JSON.stringify(content);
      }
    } else if (content) {
      contentString = String(content);
    }
    
    // Extract hashtags from the content string without modifying newlines
    const regex = /#(\w+)/g;
    let match;
    
    while ((match = regex.exec(contentString)) !== null) {
      hashtags.push(`#${match[1]}`);
    }
    
    return hashtags;
  };

  // Reset errors when form data changes
  useEffect(() => {
    setErrors({});
  }, [formData]);

  // Set language based on selected brand, with user language as fallback
  useEffect(() => {
    const availableLanguages = [
      'en', 'en-US', 'en-GB', 
      'fr', 'fr-CA', 
      'de', 'es', 'es-MX', 
      'it', 'pt', 'pt-BR', 
      'nl', 'ru', 'ar', 'zh', 'ja', 'ko', 'hi', 'bn', 'tr', 'vi', 'tl', 'fa', 'pl', 'th', 'sv', 'ro', 'uk', 'cs', 'lb', 'da', 'fi', 'no'
    ];

    let targetLanguage = 'en'; // Default fallback

    // 1. Primary: Use brand language if available
    if (formData.brandId && brands.length > 0) {
      const selectedBrand = brands.find((brand: any) => brand.id === formData.brandId);
      if (selectedBrand?.languages && Array.isArray(selectedBrand.languages) && selectedBrand.languages.length > 0) {
        const brandLanguage = selectedBrand.languages[0].toLowerCase(); // Use first language from array
        // Check if brand language is supported
        if (availableLanguages.includes(brandLanguage)) {
          targetLanguage = brandLanguage;
        } else {
          // Try language-only fallback (e.g., 'fr-FR' -> 'fr')
          const languageOnly = brandLanguage.split('-')[0];
          if (availableLanguages.includes(languageOnly)) {
            targetLanguage = languageOnly;
          }
        }
        console.log('Using brand language:', targetLanguage, 'from brand:', selectedBrand.name);
        setLanguageSetFromBrand(true);
      } else {
        setLanguageSetFromBrand(false);
      }
    }
    
    // 2. Fallback: Use user's app language if no brand language is set
    if (targetLanguage === 'en' && (!formData.brandId || !brands.find((brand: any) => brand.id === formData.brandId)?.languages?.length)) {
      const rawLanguage = (typeof i18n.language === 'string' ? i18n.language : 'en') as string;
      const normalizedLanguage = rawLanguage.toLowerCase();
      
      // First, try exact match
      if (availableLanguages.includes(normalizedLanguage)) {
        targetLanguage = normalizedLanguage;
      } else {
        // If no exact match, try language-only (e.g., 'fr-FR' -> 'fr')
        const languageOnly = normalizedLanguage.split('-')[0];
        targetLanguage = availableLanguages.includes(languageOnly) ? languageOnly : 'en';
      }
      console.log('Using user language fallback:', targetLanguage);
      setLanguageSetFromBrand(false);
    }
    
    // Update form language if it's different
    if (targetLanguage !== formData.language) {
      setFormData(prev => ({ ...prev, language: targetLanguage }));
    }
  }, [formData.brandId, brands, i18n.language]);

  // Set imageStyle based on selected brand when "With image" is selected
  useEffect(() => {
    // Only proceed if generateImage is true and we have a brand selected
    if (!formData.generateImage || !formData.brandId || !brands.length) {
      setImageStyleSetFromBrand(false);
      return;
    }

    const selectedBrand = brands.find((brand: any) => brand.id === formData.brandId);
    let targetImageStyle = 'auto'; // Default fallback

    // Check if brand has imageStyle defined
    if (selectedBrand?.imageStyle && typeof selectedBrand.imageStyle === 'string') {
      targetImageStyle = selectedBrand.imageStyle;
      console.log('Using brand imageStyle:', targetImageStyle, 'from brand:', selectedBrand.name);
      setImageStyleSetFromBrand(true);
    } else {
      console.log('No brand imageStyle found, using auto fallback');
      setImageStyleSetFromBrand(false);
    }

    // Update form imageStyle if it's different (only when generateImage is true)
    if (targetImageStyle !== formData.imageStyle) {
      setFormData(prev => ({ ...prev, imageStyle: targetImageStyle }));
    }
  }, [formData.generateImage, formData.brandId, brands]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.brandId) {
      newErrors.brandId = t('contentCreation.errors.brandRequired');
    }
    
    if (!formData.personaId) {
      newErrors.personaId = t('contentCreation.errors.personaRequired');
    }
    
    if (formData.platforms.length === 0) {
      newErrors.platforms = t('contentCreation.errors.platformsRequired');
    }
    
    // Make description optional if sourceUrl is provided
    if (!formData.shortDescription.trim() && !(formData.sourceUrl?.trim())) {
      newErrors.shortDescription = t('contentCreation.errors.descriptionOrSourceRequired');
    }
    
    if (!formData.tone) {
      newErrors.tone = t('contentCreation.errors.toneRequired');
    }
    
    if (!formData.contentLength) {
      newErrors.contentLength = t('contentCreation.errors.contentLengthRequired');
    }
    
    if (!formData.language) {
      newErrors.language = t('contentCreation.errors.languageRequired');
    }
    
    if (formData.shortDescription.length > 600) {
      newErrors.shortDescription = t('contentCreation.errors.descriptionTooLong', `Description too long (max 600 characters)`);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set the flag to allow content generation
    allowContentGeneration.current = true;
    
    if (validateForm()) {
      // IMPORTANT: This is the only place where content generation should be triggered
      // Content is only generated when the user explicitly clicks the Generate or Regenerate button
      
      // Create a copy of formData to modify before sending
      const requestData = { ...formData };
      
      // Use the value from React state, not direct DOM access
      requestData.shortDescription = formData.shortDescription;
      
      // Convert goal to lowercase if provided
      if (requestData.goal) {
        requestData.goal = requestData.goal.toLowerCase();
      }
      
      // If using extracted image, don't generate a new one
      const isUsingExtractedImage = requestData.useExtractedImage || false;
      if (isUsingExtractedImage) {
        requestData.generateImage = false;
      }
      
      // Remove useExtractedImage from request as it's only for frontend
      delete (requestData as any).useExtractedImage;
      
      // If generating an image, add the style_type parameter in uppercase
      if (requestData.generateImage && requestData.imageStyle) {
        // Ensure RENDER_3D is properly formatted from '3d'
        const styleType = requestData.imageStyle === '3d' ? 'RENDER_3D' : requestData.imageStyle.toUpperCase();
        requestData.style_type = styleType;
        
        // Add aspect_ratio parameter based on imageSize
        if (requestData.imageSize) {
          const aspectRatioMap: Record<string, string> = {
            'square': '1x1',
            '9:16': '9x16',
            '4:5': '4x5'
          };
          requestData.aspect_ratio = aspectRatioMap[requestData.imageSize] || '1x1';  // Default to square if not found
          
          // Log the mapped aspect ratio for debugging
          console.log('Using aspect ratio:', requestData.aspect_ratio, 'from size:', requestData.imageSize);
        }
      }
      
      console.log('*** CONTENT GENERATION EXPLICITLY TRIGGERED BY USER ACTION ***');
      console.log('Request data:', requestData);
      
      // Only proceed if content generation is explicitly allowed
      if (allowContentGeneration.current) {
        console.log('Content generation allowed, making API call');
        contentGeneration.mutate(requestData, {
          onSuccess: async (data) => {
            // Reset the flag after successful content generation
            allowContentGeneration.current = false;
            
            console.log('Content generation API call successful, raw response:', data);
            
            // If using extracted image, download and upload it to our storage BEFORE transforming
            if (isUsingExtractedImage && articlePreview?.image) {
              console.log('📥 Downloading and uploading extracted image:', articlePreview.image);
              try {
                // Download the image from the external URL
                const imageFile = await imageUrlToFile(articlePreview.image, 'extracted-image.jpg');
                
                if (imageFile) {
                  // Upload to our storage
                  const user = auth.currentUser;
                  if (user) {
                    const token = await user.getIdToken();
                    const uploadFormData = new FormData();
                    uploadFormData.append('file', imageFile);
                    if (formData.brandId) {
                      uploadFormData.append('brandId', formData.brandId);
                    }

                    const uploadResponse = await fetch(`${API_BASE_URL}/storage/upload`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${token}`
                      },
                      body: uploadFormData
                    });

                    if (uploadResponse.ok) {
                      const uploadResult = await uploadResponse.json();
                      const uploadedUrl = uploadResult.publicUrl || uploadResult.url || uploadResult.fileUrl || uploadResult.data?.url;
                      if (uploadedUrl) {
                        data.imageUrl = uploadedUrl;
                        console.log('✅ Image uploaded successfully:', uploadedUrl);
                      } else {
                        console.warn('⚠️ Upload succeeded but no URL returned, using original');
                        data.imageUrl = articlePreview.image;
                      }
                    } else {
                      console.error('❌ Upload failed, using original URL');
                      data.imageUrl = articlePreview.image;
                    }
                  } else {
                    console.warn('⚠️ No authenticated user, using original URL');
                    data.imageUrl = articlePreview.image;
                  }
                } else {
                  console.warn('⚠️ Failed to download image, using original URL');
                  data.imageUrl = articlePreview.image;
                }
              } catch (error) {
                console.error('❌ Error uploading extracted image:', error);
                // Fallback to original URL if upload fails
                data.imageUrl = articlePreview.image;
              }
            }
            
            // Now transform the response (posts will get the uploaded imageUrl)
            const transformedData = transformContentResponse(data);
            
            console.log('Setting generated content with transformed data:', transformedData);
            console.log('generatedContent.imageUrl:', transformedData.imageUrl);
            console.log('First post imageUrl:', transformedData.data?.contents?.[0]?.imageUrl);
            setGeneratedContent(transformedData);
            setHasGeneratedNewContent(true);
            // Persist last used image style/size if an image was requested and produced
            if ((requestData.generateImage || isUsingExtractedImage) && transformedData?.imageUrl) {
              setLastGeneratedImageStyle(formData.imageStyle || 'auto');
              setLastGeneratedImageSize((formData.imageSize as ImageSize) || '4:5');
            }
          },
          onError: (error) => {
            // Reset the flag after error
            allowContentGeneration.current = false;
            
            console.error('Content generation API call failed:', error);
            setErrors({
              submit: error.message || t('contentCreation.errors.generationFailed'),
            });
          },
        });
      } else {
        console.log('Content generation attempted but not allowed - not explicitly triggered by user');
      }
    } else {
      console.warn('Form validation failed, not proceeding with content generation');
      console.log('Current errors:', errors);
      
      // Scroll to the form actions section to show the error summary
      setTimeout(() => {
        const formActionsElement = document.querySelector('form .flex.justify-between');
        if (formActionsElement) {
          formActionsElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Add useEffect for scrolling after content generation
  useEffect(() => {
    if (hasGeneratedNewContent && resultsRef.current) {
      // Ensure the element is still in the document before scrolling
      if (document.body.contains(resultsRef.current)) {
        if (window.innerWidth >= 1024) { // lg breakpoint
          scrollToTop();
        } else {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      setHasGeneratedNewContent(false); // Reset the flag
    }
  }, [hasGeneratedNewContent, generatedContent, scrollToTop, scrollToResults]);

  // Close regenerate menu on outside click
  useEffect(() => {
    if (!isRegenerateMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (regenerateMenuRef.current && !regenerateMenuRef.current.contains(e.target as Node)) {
        setIsRegenerateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isRegenerateMenuOpen]);

  // Update the "Regenerate" button click handler to ensure it explicitly calls handleSubmit
  const handleRegenerate = () => {
    // Set the flag to allow content generation
    allowContentGeneration.current = true;
    
    // Create a synthetic event object 
    const syntheticEvent = { preventDefault: () => {} };
    // Call the regular submit handler
    handleSubmit(syntheticEvent as React.FormEvent);
  };

  // Handle text-only regeneration (preserve existing image)
  const handleRegenerateText = () => {
    if (!formData) return;
    allowContentGeneration.current = true;
    const requestData = { ...formData, generateImage: false } as ContentGenerationRequestWithStyle;
    // Ensure no image params are sent
    delete (requestData as any).style_type;
    delete (requestData as any).aspect_ratio;

    if (allowContentGeneration.current) {
      const previousImageUrl = generatedContent?.imageUrl || null;
      contentGeneration.mutate(requestData, {
        onSuccess: (data) => {
          allowContentGeneration.current = false;
          const transformedData = transformContentResponse(data);
          // Build compare items (old vs new) per platform
          const newByPlatform: Record<string, string> = {};
          const items: Array<{ platform: string; oldContent: string; newContent: string }> = [];
          const currentContents = generatedContent?.data?.contents || [];

          const getText = (content: any): string => {
            if (typeof content === 'string') return content;
            if (content && typeof content === 'object' && 'text' in content && typeof content.text === 'string') {
              return content.text as string;
            }
            return '';
          };

          const newContents = transformedData?.data?.contents || [];
          for (const newItem of newContents) {
            const platform = (newItem.platform || 'unknown').toLowerCase();
            const oldItem = currentContents.find((c) => (c.platform || '').toLowerCase() === platform);
            const oldText = oldItem ? getText(oldItem.content) : '';
            const newText = getText(newItem.content);
            if (newText) {
              newByPlatform[platform] = newText;
              items.push({ platform, oldContent: oldText, newContent: newText });
            }
          }

          // Open compare modal if we have at least one item; otherwise no change
          if (items.length > 0) {
            setCompareItems(items);
            // store mapping temporarily on instance via ref-like state
            setPendingNewTextByPlatform(newByPlatform);
            setIsCompareModalOpen(true);
          }
          // Keep last style/size as we keep the existing image
          if (previousImageUrl) {
            setLastGeneratedImageStyle(formData.imageStyle || lastGeneratedImageStyle || 'auto');
            setLastGeneratedImageSize((formData.imageSize as ImageSize) || lastGeneratedImageSize || '4:5');
          }
        },
        onError: (error: any) => {
          allowContentGeneration.current = false;
          setErrors({
            submit: error?.message || t('contentCreation.errors.generationFailed'),
          });
        },
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Track if shortDescription is being manually edited
    if (name === 'shortDescription') {
      manuallyEditedDescription.current = true;
      console.log('Description manually edited to:', value);
    }
    
    // Clear article preview when URL changes
    if (name === 'sourceUrl') {
      setArticlePreview(null);
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    // Reset the form data
    setFormData({
      brandId: '',
      personaId: '',
      platforms: [],
      topic: '',
      shortDescription: '',
      goal: '',
      tone: 'professional',
      contentLength: 'medium',
      language: 'en',
      keywords: [],
      sourceUrl: '',
      format: 'regular',
      generateImage: false,
      imageStyle: 'auto',
      imageSize: '4:5',
    });
    
    // Reset the generated content
    setGeneratedContent(null);
    
    // Reset any errors
    setErrors({});
    
    // Reset the manually edited description flag
    manuallyEditedDescription.current = false;
    
    // Reset the auto-selection flag to allow a new auto-selection after reset
    hasAutoSelectedIdea.current = false;
    
    // Reset keywords input state
    setKeywordsInputValue('');
    
    // Reset brand-based flags
    setLanguageSetFromBrand(false);
    setImageStyleSetFromBrand(false);
    
    console.log('Form reset, including manually edited description flag and auto-selection flag');
  };

  const handleCopyAll = () => {
    if (generatedContent?.data?.contents) {
      const allContent = generatedContent.data.contents
        .map((item) => {
          return `${item.platform.toUpperCase()}\n\n${item.content}${
            item.hashtags && item.hashtags.length > 0 ? '\n\n' + item.hashtags.join(' ') : ''
          }\n\n---\n\n`;
        })
        .join('');
      
      navigator.clipboard.writeText(allContent);
      setAllCopied(true);
      setTimeout(() => setAllCopied(false), 2000);
    }
  };

  const handleCreatePost = (platform: string, content: string, hashtags?: string[]) => {
    const platformContent = generatedContent?.data?.contents?.find(item => item.platform === platform);
    const imageUrl: string | null = typeof platformContent === 'object' && platformContent 
      ? ('imageUrl' in platformContent && typeof platformContent.imageUrl === 'string' ? platformContent.imageUrl : null) 
      : null;
      
    const normalizedPlatform = platform.toLowerCase() as SocialPlatform;
    
    // Check if there are other posts
    const otherPosts = generatedContent?.data?.contents?.filter(item => item.platform.toLowerCase() !== normalizedPlatform) || [];

    if (otherPosts.length > 0) {
      setSelectedPostForEditing({ platform: normalizedPlatform, content, hashtags, imageUrl });
      setIsAIPostDraftModalOpen(true);
    } else {
      // No other posts, proceed directly to editing
    navigate('/posts/new', { 
      state: { 
        content, 
        platform: normalizedPlatform, 
        hashtags,
        brandId: formData.brandId,
        imageUrl: imageUrl
      } 
    });
    }
  };

  // Handle "Discard" action from AIPostDraftModal
  const handleDiscardOtherPosts = () => {
    setIsAIPostDraftModalOpen(false);
    if (selectedPostForEditing) {
      navigate('/posts/new', { 
        state: { 
          content: selectedPostForEditing.content, 
          platform: selectedPostForEditing.platform as SocialPlatform, 
          hashtags: selectedPostForEditing.hashtags,
          brandId: formData.brandId,
          imageUrl: selectedPostForEditing.imageUrl
        } 
      });
      setSelectedPostForEditing(null);
    }
  };

  // Handle "Save as drafts" action from AIPostDraftModal
  const handleSaveOtherPostsAndProceed = async () => {
    if (!generatedContent?.data?.contents || !selectedPostForEditing) return;

    setIsSavingDrafts(true);
    const postsToSave = generatedContent.data.contents.filter(
      item => item.platform.toLowerCase() !== selectedPostForEditing?.platform.toLowerCase()
    );

    try {
      for (const post of postsToSave) {
        const postPlatformContent: any = generatedContent?.data?.contents?.find(p => p.platform === post.platform);
        
        let postImageUrl: string | null = null;
        if (postPlatformContent && 
            typeof postPlatformContent === 'object' && 
            postPlatformContent !== null && // Ensure it's not null
            postPlatformContent.hasOwnProperty('imageUrl')) {
          if (typeof postPlatformContent.imageUrl === 'string') {
            postImageUrl = postPlatformContent.imageUrl;
          } else if (postPlatformContent.imageUrl === null) {
            postImageUrl = null;
          }
        }

        let contentText = '';
        if (typeof post.content === 'string') {
          contentText = post.content;
        } else if (post.content && typeof post.content === 'object' && post.content !== null && post.content.hasOwnProperty('text')) {
          const contentAsAny = post.content as any;
          if (typeof contentAsAny.text === 'string') {
            contentText = contentAsAny.text;
          }
        }

        const draftPayload: any = {
          brandId: formData.brandId,
          title: '',
          content: contentText,
          format: formData.format || 'regular',
          platforms: [post.platform.toLowerCase() as SocialPlatform],
          needsReview: false,
          reviewerId: '',
          tags: [],
          hashtags: post.hashtags || [],
          platformSettings: {}
        };

        if (postImageUrl) {
          draftPayload.media = {
            url: postImageUrl,
            type: 'image' as const, // Assuming image, adjust if video can be generated
            thumbnailUrl: ''
          };
        }

        await saveDraft(draftPayload);
      }
      // After saving, proceed to edit the selected post
      handleDiscardOtherPosts(); // This will navigate
    } catch (error) {
      console.error('Error saving other posts as drafts:', error);
      // Optionally show a toast message for the error
      // For simplicity, we'll still proceed to edit the selected post even if some drafts fail to save.
      handleDiscardOtherPosts();
    } finally {
      setIsSavingDrafts(false);
      setIsAIPostDraftModalOpen(false);
    }
  };

  // Handle saving a draft post with image
  const handleSaveDraft = async (platform: string, content: string, hashtags?: string[], imageUrl?: string | null) => {
    console.log(`Saving draft for ${platform} with content and image`);
    
    // Vérifier si un brand est sélectionné
    if (!formData.brandId) {
      setErrors({
        ...errors,
        submit: 'Veuillez sélectionner une marque avant de sauvegarder'
      });
      return;
    }
    
    try {
      // Préparer le payload pour le service de brouillons
      const draftPayload = {
        brandId: formData.brandId,
        title: '',
        content,
        format: formData.format || 'regular', // Utiliser 'regular' comme valeur par défaut
        platforms: [platform], // Utiliser spécifiquement cette plateforme
        needsReview: false,
        reviewerId: '',
        tags: [],
        hashtags: hashtags || [],
        platformSettings: {},
        // Ajouter les informations d'image si disponibles
        ...(imageUrl && {
          media: {
            url: imageUrl,
            type: 'image' as const,
            thumbnailUrl: ''
          }
        })
      };
      
      // Utiliser le service pour sauvegarder le brouillon
      await saveDraft(draftPayload);
      
      // Réinitialiser les erreurs
      setErrors({});
      
      console.log('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      
      // Afficher l'erreur à l'utilisateur
      setErrors({
        ...errors,
        submit: `Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  };

  // Handle idea selection
  const handleIdeaSelect = (idea: any) => {
    // Track that we're selecting a content idea to avoid overwriting manual edits
    console.log('Content idea selected:', idea);
    
    // Reset the manually edited flag since we're explicitly choosing a content idea
    manuallyEditedDescription.current = false;
    
    let newTopic = '';
    let newDescription = '';
    let newGoal = '';
    
    // Extract the title and description based on the format of the idea
    if (typeof idea === 'object' && idea !== null) {
      newTopic = idea.title || '';
      newDescription = idea.description || '';
      // Normalize goal to lowercase to match dropdown values (API might return "Inform" but dropdown expects "inform")
      newGoal = idea.goal ? idea.goal.toLowerCase() : '';
    } else {
      // If it's just a string, treat it as the description
      newDescription = idea;
    }
    
    // Update the form data state
    setFormData(prev => ({
      ...prev,
      topic: newTopic,
      shortDescription: newDescription,
      goal: newGoal || prev.goal // Keep existing goal if idea doesn't have one
    }));
    
    // No need to directly manipulate the DOM anymore
    console.log('Updated description via state with:', newDescription);
    if (newGoal) {
      console.log('Updated goal with:', newGoal);
    }
  };

  // Handle purging description and deselecting content idea
  const handlePurgeDescription = () => {
    console.log('Purging description and deselecting content idea');
    
    // Clear the description and topic
    setFormData(prev => ({
      ...prev,
      topic: '',
      shortDescription: ''
    }));
    
    // Reset the manually edited flag
    manuallyEditedDescription.current = false;
  };

  // Handle keywords input
  const handleKeywordsInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && keywordsInputValue.trim()) {
      e.preventDefault();
      const newKeyword = keywordsInputValue.trim();
      
      // Add the keyword if it's not already in the list
      if (!formData.keywords.includes(newKeyword)) {
        setFormData(prev => ({
          ...prev,
          keywords: [...prev.keywords, newKeyword]
        }));
      }
      
      // Clear the input
      setKeywordsInputValue('');
    }
  };

  // Card style with black stroke
  const cardStyle = "bg-white rounded-lg shadow-md p-6 border border-black";
  const cardHeaderStyle = "flex items-center gap-2 mb-4 pb-2 border-b border-gray-200";
  const cardTitleStyle = "text-lg font-semibold text-gray-800";

  // Add a function to handle generating more ideas
  const handleGenerateMoreIdeas = async () => {
    if (isGeneratingMoreIdeas) return;
    
    console.log('🎲 Manually generating more ideas...');
    try {
      await generateMoreIdeas();
      console.log('✅ More ideas generated successfully');
    } catch (error) {
      console.error('❌ Failed to generate more ideas:', error);
    }
  };

  // Extract a YouTube video ID from the common URL shapes (watch, youtu.be,
  // embed, shorts, live). Returns null when the URL isn't a recognizable
  // YouTube video.
  const getYouTubeVideoId = (rawUrl: string): string | null => {
    if (!rawUrl) return null;
    try {
      const url = new URL(rawUrl.trim());
      const host = url.hostname.replace(/^www\./, '').toLowerCase();

      if (host === 'youtu.be') {
        const id = url.pathname.split('/').filter(Boolean)[0];
        return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
      }

      if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
        const v = url.searchParams.get('v');
        if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v;

        const segments = url.pathname.split('/').filter(Boolean);
        // /embed/{id}, /shorts/{id}, /live/{id}
        if (segments.length >= 2 && ['embed', 'shorts', 'live'].includes(segments[0])) {
          const id = segments[1];
          return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  // Handle URL check/summarize
  const handleCheckUrl = async () => {
    if (!formData.sourceUrl?.trim()) {
      return;
    }

    setIsCheckingUrl(true);
    setArticlePreview(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Get the language - use app language
      const languageToSend = appLanguage;
      const normalizedLanguage = normalizeLanguage(languageToSend);

      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/ai-coach/article/summarize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: formData.sourceUrl.trim(),
          language: normalizedLanguage,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to summarize article: ${response.status} ${errorText}`);
      }

      const data = await response.json();

      if (data.summary) {
        // The article summarizer does not return an og:image for YouTube
        // URLs, so fall back to YouTube's public thumbnail CDN so the
        // preview card matches the article experience.
        const youtubeVideoId = getYouTubeVideoId(formData.sourceUrl.trim());
        const youtubeThumbnail = youtubeVideoId
          ? `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`
          : undefined;

        const preview = {
          image:
            data.summary.metadata?.image ||
            data.summary.visual_elements?.metadata?.image ||
            youtubeThumbnail,
          recap: data.summary.recap,
          title: data.summary.metadata?.title,
          description: data.summary.metadata?.description,
        };

        setArticlePreview(preview);
        
        // Always replace description with recap when URL is checked
        // Limit to 550 characters to avoid exceeding the form limit
        if (preview.recap) {
          const truncatedRecap = preview.recap.length > 550 
            ? preview.recap.substring(0, 550).trim() + '...'
            : preview.recap;
          
          setFormData(prev => ({
            ...prev,
            shortDescription: truncatedRecap,
          }));
          manuallyEditedDescription.current = false;
        }
      }
    } catch (error) {
      console.error('Error checking URL:', error);
      alert(t('contentCreation.form.urlCheckError', 'Failed to check URL. Please try again.'));
    } finally {
      setIsCheckingUrl(false);
    }
  };

  // Handle image regeneration
  const handleImageRegenerate = (imageStyle: string, imageSize: ImageSize) => {
    if (!generatedContent?.data?.contents || !formData.brandId || !formData.personaId) {
      console.error('Missing required data for image regeneration');
      return;
    }

    // Get the first post content for image generation
    const firstPost = generatedContent.data.contents[0];
    let postContent = '';
    
    if (typeof firstPost.content === 'string') {
      postContent = firstPost.content;
    } else if (firstPost.content && typeof firstPost.content === 'object' && 'text' in firstPost.content) {
      postContent = (firstPost.content as any).text || '';
    }

    if (!postContent) {
      console.error('No post content found for image regeneration');
      return;
    }

    const request = {
      postContent,
      brandId: formData.brandId,
      personaId: formData.personaId,
      aspect_ratio: convertImageSizeToAspectRatio(imageSize),
      style_type: convertImageStyleToStyleType(imageStyle),
    };

    imageRegeneration.mutate(request, {
      onSuccess: (response) => {
        // Store the generated image temporarily for validation
        if (response.imageUrl) {
          setTemporaryGeneratedImageUrl(response.imageUrl);
        }
      },
      onError: (error) => {
        console.error('Image regeneration failed:', error);
        setErrors({
          ...errors,
          submit: error.message || 'Image regeneration failed'
        });
      }
    });
  };

  // Handle image validation (user confirms they want to use the new image)
  const handleImageValidation = (imageUrl: string) => {
    if (generatedContent) {
      // Update both the main imageUrl and all platform-specific imageUrls
      const updatedContent = {
        ...generatedContent,
        imageUrl: imageUrl,
        data: {
          ...generatedContent.data,
          contents: generatedContent.data?.contents?.map(content => ({
            ...content,
            imageUrl: imageUrl
          })) || []
        }
      };
      
      setGeneratedContent(updatedContent);
      console.log('✅ Image updated for all platforms:', imageUrl);
    }
    
    // Reset temporary state and close modal
    setTemporaryGeneratedImageUrl(null);
    setIsImageRegenerationModalOpen(false);
  };

  // Example: Add cleanup to effects that process content or set state after async work
  useEffect(() => {
    let isMounted = true;
    // Example async effect (replace with actual async logic if present)
    const processContent = async () => {
      // ... your async logic ...
      if (isMounted) {
        // setProcessedContent(result); // Example state update
      }
    };
    // if (content) processContent(); // Uncomment and adapt as needed
    return () => {
      isMounted = false;
    };
  }, [/* dependencies for async effect, e.g. content, language */]);



  return (
    <>
      <PageLayout
        title={t('contentCreation.title')}
        subtitle={t('contentCreation.subtitle')}
      >

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Section */}
            <div>
              <form 
                onSubmit={(e) => {
                  console.log('Form submit event triggered');
                  e.preventDefault();
                  handleSubmit(e);
                }} 
                className="space-y-6"
              >
                {/* Card 1: Audience */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <Users className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>Audience</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Brand Selection */}
                    <div>
                      <label htmlFor="brandId" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.brand')} <span className="text-red-500">*</span>
                      </label>
                      
                      {/* Brand Grid Selection */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {brands.map((brand: any) => (
                          <button
                            key={brand.id}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, brandId: brand.id }))}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg border border-black ${
                              formData.brandId === brand.id
                                ? 'border-primary-600 bg-primary-50'
                                : 'hover:border-primary-600'
                            }`}
                          >
                            <img 
                              src={brand.logo} 
                              alt={brand.name}
                              className="w-10 h-10 rounded-lg object-cover"
                              onError={(e) => {
                                // Handle image loading errors
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <span className="text-sm text-gray-700 truncate max-w-full">{brand.name}</span>
                          </button>
                        ))}
                      </div>
                      {errors.brandId && <p className="mt-1 text-sm text-red-600">{errors.brandId}</p>}
                    </div>

                    {/* Persona Selection */}
                    <div>
                      <label htmlFor="personaId" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.persona')} <span className="text-red-500">*</span>
                      </label>
                      
                      {/* Persona Grid Selection */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {isLoadingPersonas ? (
                          // Loading state - show skeleton loaders
                          Array(2).fill(0).map((_, index) => (
                            <div 
                              key={index}
                              className="flex items-start p-3 rounded-lg border border-gray-200 animate-pulse"
                            >
                              <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0"></div>
                              <div className="ml-3 text-left w-full">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                              </div>
                            </div>
                          ))
                        ) : personas.length > 0 ? (
                          // Show personas grid if we have personas
                          personas.map((persona: any) => {
                            // Function to get language emoji
                            const getLanguageEmoji = (lang: string) => {
                              switch (lang.toLowerCase()) {
                                case 'en':
                                  return '🇬🇧';
                                case 'fr':
                                  return '🇫🇷';
                                case 'es':
                                  return '🇪🇸';
                                default:
                                  return '🌐';
                              }
                            };
                            
                            return (
                              <button
                                key={persona.id}
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, personaId: persona.id }))}
                                className={`flex items-start p-3 rounded-lg border border-black ${
                                  formData.personaId === persona.id
                                    ? 'border-primary-600 bg-primary-50'
                                    : 'hover:border-primary-600'
                                }`}
                              >
                                {/* Left column - Avatar */}
                                {persona.avatarUrl ? (
                                  <img 
                                    src={persona.avatarUrl} 
                                    alt={persona.name}
                                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                    onError={(e) => {
                                      // Handle image loading errors
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg border border-gray-200 bg-primary-50 flex items-center justify-center flex-shrink-0">
                                    <User className="w-6 h-6 text-primary-600" />
                                  </div>
                                )}
                                
                                {/* Right column - Details */}
                                <div className="ml-3 text-left">
                                  <span className="text-sm font-medium text-gray-800 block">
                                    {persona.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">
                                    {persona.jobTitle}
                                  </span>
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className="text-xs text-gray-500">{persona.age}</span>
                                    {persona.languages && persona.languages.map((lang: string, index: number) => (
                                      <span key={index} className="text-xs">
                                        {getLanguageEmoji(lang)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          // Show empty state if no personas
                          <div className="col-span-2 p-4 border border-dashed border-gray-300 rounded-lg text-center">
                            <p className="text-sm text-gray-500 mb-3">
                              {t('contentCreation.form.noPersonas', 'No personas available for this brand')}
                            </p>
                            <Link 
                              to="/ai-personas-builder" 
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              {t('contentCreation.form.createPersona', 'Create a Persona')}
                            </Link>
                          </div>
                        )}
                      </div>
                      {errors.personaId && <p className="mt-1 text-sm text-red-600">{errors.personaId}</p>}
                      
                      {/* Only show the dropdown if explicitly needed for accessibility */}
                      <div className="sr-only">
                        <select
                          id="personaId"
                          name="personaId"
                          value={formData.personaId}
                          onChange={handleInputChange}
                          aria-hidden="true"
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                        >
                          <option value="">{t('contentCreation.form.selectPersona')}</option>
                          {personas.map((persona: any) => (
                            <option key={persona.id} value={persona.id}>
                              {persona.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Platforms */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <Share2 className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('cards.platforms.title', 'Platforms')}</h2>
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('contentCreation.form.platforms')} <span className="text-red-500">*</span>
                    </label>
                    
                    {/* Platforms Grid Selection */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {socialPlatforms.map(platform => {
                        if (platform.id === 'all') return null;
                        
                        const platformId = platform.id as string;
                        const isSelected = formData.platforms.includes(platformId);
                        const connectedAccount = connectedAccounts.find((acc: any) => acc.platform === platformId);
                        
                        return (
                          <button
                            key={platform.id}
                            type="button"
                            onClick={() => {
                              if (!formData.platforms.includes(platformId)) {
                                setFormData((prev) => ({ 
                                  ...prev, 
                                  platforms: [...prev.platforms, platformId] 
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  platforms: prev.platforms.filter(p => p !== platformId)
                                }));
                              }
                            }}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg border border-black ${
                              isSelected
                                ? 'border-primary-600 bg-primary-50'
                                : 'hover:border-primary-600'
                            }`}
                          >
                            {connectedAccount ? (
                              <>
                                <SocialProfilePicture
                                  pictureUrl={connectedAccount.picture_url}
                                  username={connectedAccount.username}
                                  platform={platform.id as SocialPlatform}
                                  size="md"
                                />
                                <span className="text-xs text-gray-700 text-center line-clamp-2 overflow-hidden whitespace-normal break-words">
                                  @{connectedAccount.username}
                                </span>
                              </>
                            ) : (
                              <>
                                <SocialIcon platform={platform.id as SocialPlatform} size={24} />
                                <span className="text-sm text-gray-700">{platform.name}</span>
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {errors.platforms && <p className="mt-1 text-sm text-red-600">{errors.platforms}</p>}
                    
                    {/* Format Selection - Hidden for now, always use 'regular' */}
                    {/* 
                    TODO: Uncomment when we want to support multiple formats
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.format', 'Content Format')}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, format: 'regular' }))}
                          className={`flex items-center gap-3 p-3 rounded-lg border border-black ${
                            formData.format === 'regular'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'hover:border-primary-600'
                          }`}
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                            <span className="text-xl">📸</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-sm">{t('postContent.regularFormat', 'Classic (Horizontal)')}</div>
                            <div className="text-xs text-gray-500">{t('contentCreation.formats.regularDescription', 'Standard posts for your feed')}</div>
                          </div>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, format: 'reel' }))}
                          className={`flex items-center gap-3 p-3 rounded-lg border border-black ${
                            formData.format === 'reel'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'hover:border-primary-600'
                          }`}
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                            <span className="text-xl">📱</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-sm">{t('postContent.reelFormat', 'Reel (Vertical)')}</div>
                            <div className="text-xs text-gray-500">{t('contentCreation.formats.reelDescription', 'Short-form video content')}</div>
                          </div>
                        </button>
                      </div>
                    </div>
                    */}
                  </div>
                </div>

                {/* Card 3: Source */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <LinkIcon className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.sourceCard', 'Source')}</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Content Ideas - Always visible */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('contentCreation.form.contentIdeas', 'Content Ideas')}
                        </label>
                        <button
                          type="button"
                          onClick={handleGenerateIdeas}
                          disabled={isIdeasLoading || isGeneratingMoreIdeas || !formData.brandId || !formData.personaId}
                          className={`inline-flex items-center px-3 py-1.5 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed ${
                            isIdeasLoading || isGeneratingMoreIdeas ? 'bg-gray-400 hover:bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
                          }`}
                        >
                          {isIdeasLoading || isGeneratingMoreIdeas ? (
                            <>
                              <div className="w-4 h-4 mr-1.5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                              <span className="text-sm font-semibold">{t('common.generating', 'Generating...')}</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-1.5" />
                              <span className="text-sm font-semibold">
                                {shouldLoadIdeas 
                                  ? t('contentCreation.form.generateMoreIdeas', 'Generate 5 more ideas')
                                  : t('contentCreation.form.generateIdeas', 'Generate ideas')
                                }
                              </span>
                            </>
                          )}
                        </button>
                      </div>
                      
                      {!formData.brandId || !formData.personaId ? (
                        <p className="text-sm text-gray-500">
                          {t('contentCreation.form.selectBrandAndPersona', 'Please select a brand and persona to generate ideas')}
                        </p>
                      ) : (isIdeasLoading && !shouldLoadIdeas) ? (
                        <p className="text-sm text-gray-500">
                          {t('contentCreation.form.clickToGenerate', 'Click "Generate ideas" to get started')}
                        </p>
                      ) : (isIdeasLoading) ? (
                        <div className="grid grid-cols-1 gap-2">
                          {Array(5).fill(0).map((_, index) => (
                            <div key={index} className="flex items-center w-full px-3 py-2 rounded-md text-sm border border-gray-200 animate-pulse">
                              <div className="h-3 w-3 bg-gray-300 rounded-sm mr-2 flex-shrink-0"></div>
                              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                            </div>
                          ))}
                        </div>
                      ) : hasIdeasError ? (
                        <div>
                          <p className="text-sm text-red-500 mb-2">
                            {ideasErrorMessage ? `Error: ${ideasErrorMessage}` : t('contentCreation.form.ideasError', 'Failed to load content ideas')}
                          </p>

                          <button
                            type="button"
                            onClick={() => refetchIdeas()}
                            className="text-xs text-primary-600 hover:text-primary-800"
                          >
                            {t('common.retry', 'Retry')}
                          </button>
                        </div>
                      ) : contentIdeas.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2">
                          {contentIdeas.map((idea: any, index: number) => {
                            // Handle both string and object formats
                            const title = typeof idea === 'object' && idea !== null ? idea.title : idea;
                            const goal = typeof idea === 'object' && idea !== null ? idea.goal : null;
                            
                            // Translate goal for display
                            const formattedGoal = goal 
                              ? t(`contentCreation.form.goals.${goal.toLowerCase()}`, goal)
                              : null;
                            
                            // Check if this idea is currently selected
                            const isSelected = 
                              (typeof idea === 'object' && idea !== null && 
                               idea.title === formData.topic && 
                               idea.description === formData.shortDescription) ||
                              (typeof idea === 'string' && idea === formData.shortDescription);
                            
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleIdeaSelect(idea)}
                                className={`flex items-center w-full px-3 py-2 rounded-md text-sm border transition-colors ${
                                  isSelected 
                                    ? 'bg-primary-50 text-primary-700 border-primary-200 hover:bg-primary-100' 
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <Tag className={`h-3 w-3 min-w-3 mr-2 flex-shrink-0 ${
                                  isSelected ? 'text-primary-600' : 'text-gray-400'
                                }`} />
                                <span 
                                  className="text-left truncate w-full flex items-center gap-1" 
                                  style={{ textTransform: 'none' }} // Ensure no text transformation
                                >
                                  <span className="truncate">{title}</span>
                                  {formattedGoal && (
                                    <span className="italic text-gray-500 flex-shrink-0">
                                      ({formattedGoal})
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : shouldLoadIdeas ? (
                        <p className="text-sm text-gray-500">
                          {t('contentCreation.form.noIdeas', 'No content ideas available')}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">
                          {t('contentCreation.form.clickToGenerate', 'Click "Generate ideas" to get started')}
                        </p>
                      )}
                    </div>

                    {/* Source URL */}
                    <div>
                      <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.sourceUrl')}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          id="sourceUrl"
                          name="sourceUrl"
                          value={formData.sourceUrl}
                          onChange={handleInputChange}
                          placeholder={t('contentCreation.form.sourceUrlPlaceholder', 'Enter source URL (optional)')}
                          className="mt-1 flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={handleCheckUrl}
                          disabled={!formData.sourceUrl?.trim() || isCheckingUrl}
                          className="mt-1 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isCheckingUrl ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              {t('contentCreation.form.add', 'Add')}
                            </>
                          )}
                        </button>
                      </div>
                      {articlePreview && (
                        <div className="mt-3 flex gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          {articlePreview.image && (
                            <div className="flex-shrink-0">
                              <img
                                src={articlePreview.image}
                                alt={articlePreview.title || 'Article preview'}
                                className="w-32 h-32 object-cover rounded-lg"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {articlePreview.title && (
                              <h4 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                                {articlePreview.title}
                              </h4>
                            )}
                            {articlePreview.description && (
                              <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                                {articlePreview.description}
                              </p>
                            )}
                            {articlePreview.recap && (
                              <p className="text-xs text-gray-700 line-clamp-3">
                                {articlePreview.recap}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>

                {/* Card 4: Content */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <FileText className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.contentCard', 'Content')}</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Goal */}
                    <div>
                      <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.goal', 'Goal')}
                      </label>
                      <ContentGoalSelect
                        value={formData.goal || ''}
                        onChange={(goal) => {
                          setFormData((prev) => ({ ...prev, goal }));
                        }}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700">
                          {t('contentCreation.form.description')}
                        </label>
                        <span className={`text-xs ${formData.shortDescription.length > 600 ? 'text-red-600' : 'text-gray-500'}`}>
                          {formData.shortDescription.length}/600 {t('common.characters', 'characters')}
                        </span>
                      </div>
                      <div className="relative">
                        <MemoizedDescriptionTextarea
                          value={formData.shortDescription}
                          onChange={(e) => {
                            manuallyEditedDescription.current = true;
                            handleInputChange(e);
                          }}
                          placeholder={t('contentCreation.form.descriptionPlaceholder')}
                        />
                        
                        {/* Section bottom avec égaliseur, timer et bouton */}
                        <div className="absolute bottom-2 left-2 right-2 flex items-end justify-end">
                          {/* Égaliseur audio animé - Prend l'espace disponible */}
                          {isListening && (
                            <div className="flex items-end justify-between gap-0.5 h-8 flex-1 mr-2 overflow-hidden">
                              {Array.from({ length: 60 }).map((_, i) => {
                                // Générer hauteurs et durées variées pour 60 barres
                                const heights = [2, 6, 10, 14, 18, 22, 26, 30, 32, 34, 36, 38, 40, 42, 44, 46, 44, 42, 40, 38, 36, 34, 32, 30, 28, 26, 24, 22, 20, 18, 16, 14, 12, 10, 8, 6, 4, 6, 8, 10, 12, 16, 20, 24, 28, 32, 36, 40, 44, 46, 42, 38, 34, 30, 26, 22, 18, 14, 10, 6];
                                const durations = [474, 433, 407, 458, 400, 427, 441, 419, 487, 442, 463, 428, 445, 412, 489, 435, 456, 421, 467, 439, 451, 417, 473, 429, 448, 414, 485, 437, 459, 423, 469, 441, 453, 419, 475, 431, 461, 425, 471, 443, 455, 421, 477, 433, 463, 427, 479, 445, 465, 449, 432, 456, 411, 478, 434, 462, 426, 470, 444, 454];
                                
                                return (
                                  <div
                                    key={i}
                                    className="flex-1 bg-green-500 rounded-sm opacity-35 min-w-0"
                                    style={{
                                      height: `${heights[i]}px`,
                                      animation: `sound ${durations[i]}ms -800ms linear infinite alternate`
                                    }}
                                  />
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Timer + colonne drapeau/bouton micro */}
                          <div className="flex items-end gap-2 flex-shrink-0">
                            {/* Timer - Affiché seulement pendant l'écoute */}
                            {isListening && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded-md text-sm font-mono text-green-700">
                                <span className="text-green-500">⏱️</span>
                                <span>{formatElapsed(elapsedSeconds)}</span>
                              </div>
                            )}

                            {/* Colonne: drapeau au-dessus du bouton micro */}
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center justify-center w-8 h-8 text-lg bg-white border border-gray-200 rounded-md shadow-sm">
                                {getLanguageFlag(formData.language || 'fr-FR')}
                              </div>

                              {/* Bouton microphone */}
                              <SpeechToTextButton
                                currentLanguage={formData.language || 'fr-FR'}
                                onTextReceived={(transcript) => {
                                  const newText = formData.shortDescription
                                    ? `${formData.shortDescription} ${transcript}`
                                    : transcript;
                                  setFormData(prev => ({ ...prev, shortDescription: newText }));
                                  manuallyEditedDescription.current = true;
                                }}
                                onListeningChange={(listening) => {
                                  setIsListening(listening);
                                  if (listening) {
                                    setListeningStartTs(Date.now());
                                    setElapsedSeconds(0);
                                    // start timer
                                    if (listeningTimerRef.current) cancelAnimationFrame(listeningTimerRef.current);
                                    const tick = () => {
                                      setElapsedSeconds(Math.floor(((Date.now() - (listeningStartTs || Date.now())) / 1000)));
                                      listeningTimerRef.current = requestAnimationFrame(tick);
                                    };
                                    listeningTimerRef.current = requestAnimationFrame(tick);
                                  } else {
                                    if (listeningTimerRef.current) cancelAnimationFrame(listeningTimerRef.current);
                                    listeningTimerRef.current = null;
                                  }
                                }}
                                isDisabled={formData.shortDescription.length >= 600}
                                placeholder="Dicter la description"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Indicateur de langue pour la reconnaissance */}
                      <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                        <span>🎤 Reconnaissance vocale en:</span>
                        <span className="font-medium">
                          {formData.language === 'fr-FR' ? 'Français' :
                           formData.language === 'en-US' ? 'English (US)' :
                           formData.language === 'es-ES' ? 'Español' :
                           formData.language === 'de-DE' ? 'Deutsch' :
                           'Langue actuelle'}
                        </span>
                      </div>

                      <div className="flex justify-between items-center mt-1">
                        <div className="flex-1">
                          {errors.shortDescription && (
                            <p className="text-sm text-red-600">{errors.shortDescription}</p>
                          )}
                          {manuallyEditedDescription.current && !errors.shortDescription && (
                            <p className="text-xs text-blue-600">
                              {t('contentCreation.form.manuallyEdited', 'You have manually edited this description')}
                            </p>
                          )}
                        </div>
                        {(formData.shortDescription || formData.topic) && (
                          <button
                            type="button"
                            onClick={handlePurgeDescription}
                            className="text-sm text-primary-600 hover:text-primary-800 font-medium transition-colors ml-4"
                          >
                            {t('common.purge')}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Keywords */}
                    <div>
                      <label htmlFor="keywords" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.keywords')}
                      </label>
                      <TagInput
                        tags={formData.keywords}
                        onTagsChange={(keywords) => setFormData((prev) => ({ ...prev, keywords }))}
                        placeholder={t('contentCreation.form.keywordsPlaceholder')}
                        inputValue={keywordsInputValue}
                        onInputChange={setKeywordsInputValue}
                        onInputKeyDown={handleKeywordsInputKeyDown}
                      />
                    </div>

                    {/* Content Length */}
                    <div>
                      <label htmlFor="contentLength" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.contentLength')} <span className="text-red-500">*</span>
                      </label>
                      
                      {/* Content Length Grid Selection */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { 
                            value: 'short' as 'short', 
                            label: t('contentCreation.contentLength.short', 'Short'), 
                            emoji: '📱', 
                            description: t('contentCreation.contentLength.shortDescription', 'Brief content, ideal for quick engagement')
                          },
                          { 
                            value: 'medium' as 'medium', 
                            label: t('contentCreation.contentLength.medium', 'Medium'), 
                            emoji: '📄', 
                            description: t('contentCreation.contentLength.mediumDescription', 'Balanced length for most platforms')
                          },
                          { 
                            value: 'long' as 'long', 
                            label: t('contentCreation.contentLength.long', 'Long'), 
                            emoji: '📚', 
                            description: t('contentCreation.contentLength.longDescription', 'In-depth content with detailed information')
                          }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, contentLength: option.value }))}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              formData.contentLength === option.value
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-300 hover:border-primary-600'
                            }`}
                          >
                            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                              <span className="text-xl">{option.emoji}</span>
                            </div>
                            <div className="text-left">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                      {errors.contentLength && <p className="mt-1 text-sm text-red-600">{errors.contentLength}</p>}
                    </div>
                  </div>
                </div>

                {/* Card 4: Voice */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <MessageSquare className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.voice', 'Voice')}</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Tone */}
                    <div>
                      <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.tone')} <span className="text-red-500">*</span>
                      </label>
                      <MemoizedContentToneSelect
                        value={formData.tone}
                        onChange={(tone) => {
                          console.log('ContentToneSelect onChange triggered with value:', tone);
                          setFormData((prev) => ({ ...prev, tone }));
                          // Explicitly log that we don't want to generate content here
                          console.log('Form data updated for tone, NO CONTENT GENERATION should be triggered');
                        }}
                      />
                      {errors.tone && <p className="mt-1 text-sm text-red-600">{errors.tone}</p>}
                    </div>

                    {/* Language */}
                    <div>
                      <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.language')} <span className="text-red-500">*</span>
                      </label>
                      <MemoizedLanguageDropdown
                        value={formData.language || 'en'}
                        onChange={(language) => {
                          console.log('Language changed to:', language);
                          setFormData((prev) => ({ ...prev, language }));
                          setLanguageSetFromBrand(false); // Reset when user manually changes language
                        }}
                        buttonLabel={t('contentCreation.form.selectLanguage')}
                      />
                      {errors.language && <p className="mt-1 text-sm text-red-600">{errors.language}</p>}
                      {languageSetFromBrand && !errors.language && (
                        <p className="mt-1 text-sm text-emerald-600 font-medium">
                          ✓ {t('contentCreation.form.languageSetFromBrand', 'We have selected this default language based on the language that you put in your brand analysis')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card 5: Post Image */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <Image className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.postImage', 'Post Image')}</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="imageSelection" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.imageSelection', 'Image Selection')}
                      </label>
                      
                      {/* Image Selection Grid */}
                      <div className={`grid grid-cols-1 ${articlePreview?.image ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}>
                        {[
                          { 
                            value: 'none', 
                            label: t('contentCreation.form.textOnly', 'Text Only'), 
                            emoji: '📝', 
                            description: t('contentCreation.form.textOnlyDescription', 'Generate content without an image')
                          },
                          { 
                            value: 'generate', 
                            label: t('contentCreation.form.withImage', 'With an Image'), 
                            emoji: '🖼️', 
                            description: t('contentCreation.form.withImageDescription', 'Generate content with a related image')
                          },
                          ...(articlePreview?.image ? [{
                            value: 'extracted',
                            label: t('contentCreation.form.useExtractedImage', 'Use Existing Image'),
                            emoji: '🔗',
                            description: t('contentCreation.form.useExtractedImageDescription', 'Use the image extracted from your link')
                          }] : [])
                        ].map((option) => {
                          const isSelected = 
                            (option.value === 'none' && !formData.generateImage && !formData.useExtractedImage) ||
                            (option.value === 'generate' && formData.generateImage && !formData.useExtractedImage) ||
                            (option.value === 'extracted' && formData.useExtractedImage);
                          
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                // Update the formData state based on selection
                                if (option.value === 'none') {
                                  setFormData(prev => ({ ...prev, generateImage: false, useExtractedImage: false }));
                                } else if (option.value === 'generate') {
                                  setFormData(prev => ({ ...prev, generateImage: true, useExtractedImage: false }));
                                } else if (option.value === 'extracted') {
                                  setFormData(prev => ({ ...prev, generateImage: false, useExtractedImage: true }));
                                }
                              }}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                isSelected
                                  ? 'border-primary-600 bg-primary-50 text-primary-700'
                                  : 'border-gray-300 hover:border-primary-600'
                              }`}
                            >
                              <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                                <span className="text-xl">{option.emoji}</span>
                              </div>
                              <div className="text-left">
                                <div className="font-medium">{option.label}</div>
                                <div className="text-xs text-gray-500">{option.description}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Show Image Style selection only when "With an Image" is selected */}
                    {formData.generateImage && (
                      <div className="mt-4">
                        <label htmlFor="imageStyle" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('contentCreation.form.imageStyle')}
                        </label>
                        <MemoizedImageStyleSelect 
                          value={formData.imageStyle || 'auto'}
                          onChange={(imageStyle) => {
                            console.log('ImageStyleSelect onChange triggered with value:', imageStyle);
                            // Only update the formData state, don't trigger content generation
                            setFormData(prev => ({ ...prev, imageStyle }));
                            setImageStyleSetFromBrand(false); // Reset when user manually changes imageStyle
                          }}
                        />
                        {imageStyleSetFromBrand && formData.generateImage && (
                          <p className="mt-1 text-sm text-emerald-600 font-medium">
                            ✓ {t('contentCreation.form.imageStyleSetFromBrand', 'We have selected this default image style based on the image style that you put in your brand analysis')}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Show Image Size selection only when "With an Image" is selected */}
                    {formData.generateImage && (
                      <div className="mt-4">
                        <label htmlFor="imageSize" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('contentCreation.form.imageSize', 'Image Size')}
                        </label>
                        <MemoizedImageSizeSelect 
                          value={formData.imageSize as any || '4:5'}
                          onChange={(imageSize) => {
                            console.log('ImageSizeSelect onChange triggered with value:', imageSize);
                            // Only update the formData state, don't trigger content generation
                            setFormData(prev => ({ ...prev, imageSize }));
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-sm text-red-600">{errors.submit}</p>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {t('contentCreation.form.reset')}
                  </button>
                  <button
                    type="submit"
                    disabled={contentGeneration.isPending || formData.shortDescription.length > 600}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {contentGeneration.isPending ? (
                      <>
                        <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        {t('contentCreation.form.generating')}
                      </>
                    ) : (
                      t('contentCreation.form.generate')
                    )}
                  </button>
                </div>
                
                {/* Validation Errors Summary */}
                {Object.keys(errors).length > 0 && !errors.submit && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <h3 className="text-sm font-medium text-red-800 mb-2">
                      {t('contentCreation.form.errorSummary', 'Please fix the following errors:')}
                    </h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {Object.entries(errors).map(([field, message]) => (
                        <li key={field} className="text-sm text-red-600">
                          {message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </form>
            </div>

            {/* Results Section */}
            <div ref={resultsRef}>
              <div className="bg-white rounded-lg shadow-md p-6 border border-black h-full">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {t('contentCreation.results.title')}
                  </h2>
                  {generatedContent?.data?.contents && generatedContent.data.contents.length > 0 && (
                    <div className="flex space-x-2" ref={regenerateMenuRef}>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsRegenerateMenuOpen((o) => !o)}
                          disabled={contentGeneration.isPending}
                          className="inline-flex items-center px-3 py-1.5 border border-primary-600 shadow-sm text-sm font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('contentCreation.results.regenerate', 'Régénérer')}
                        >
                          <RefreshCw className="h-4 w-4 mr-1.5" />
                          {t('contentCreation.results.regenerate', 'Régénérer')}
                        </button>
                        {isRegenerateMenuOpen && (
                          <div className="absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                            <div className="py-1">
                              <button
                                type="button"
                                className="group w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50"
                                onClick={() => {
                                  setIsRegenerateMenuOpen(false);
                                  setIsImageRegenerationModalOpen(true);
                                }}
                              >
                                <Image className="w-4 h-4 text-gray-500 group-hover:text-primary-600" />
                                <span>{t('contentCreation.results.regenerateImage', "Régénérer l'image")}</span>
                              </button>
                              <button
                                type="button"
                                className="group w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50"
                                onClick={() => {
                                  setIsRegenerateMenuOpen(false);
                                  handleRegenerateText();
                                }}
                              >
                                <FileText className="w-4 h-4 text-gray-500 group-hover:text-primary-600" />
                                <span>{t('contentCreation.results.regenerateText', 'Régénérer le texte')}</span>
                              </button>
                              <button
                                type="button"
                                className="group w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50"
                                onClick={() => {
                                  setIsRegenerateMenuOpen(false);
                                  handleRegenerate();
                                }}
                              >
                                <RefreshCw className="w-4 h-4 text-gray-500 group-hover:text-primary-600" />
                                <span>{t('contentCreation.results.regenerateBoth', 'Régénérer les deux')}</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        disabled={isSavingDrafts}
                        onClick={async () => {
                          if (!generatedContent?.data?.contents || !formData.brandId) return;
                          setIsSavingDrafts(true);
                          try {
                            for (const post of generatedContent.data.contents) {
                              const normalizedPlatform = (post.platform || '').toLowerCase() as SocialPlatform;
                              let contentText = '';
                              if (typeof post.content === 'string') contentText = post.content;
                              else if (post.content && typeof post.content === 'object' && 'text' in post.content) contentText = (post.content as any).text || '';
                              let postImageUrl: string | null = null;
                              if (post && typeof post === 'object' && 'imageUrl' in post && typeof (post as any).imageUrl === 'string') {
                                postImageUrl = (post as any).imageUrl as string;
                                console.log('📷 Using post-specific image:', postImageUrl);
                              } else if (generatedContent.imageUrl) {
                                postImageUrl = generatedContent.imageUrl;
                                console.log('📷 Using global generatedContent.imageUrl:', postImageUrl);
                              } else {
                                console.log('⚠️ No image found for post:', post.platform);
                              }
                              const draftPayload: any = {
                                brandId: formData.brandId,
                                title: '',
                                content: contentText,
                                format: formData.format || 'regular',
                                platforms: [normalizedPlatform],
                                needsReview: false,
                                reviewerId: '',
                                tags: [],
                                hashtags: post.hashtags || [],
                                platformSettings: {},
                                ...(postImageUrl && { media: { url: postImageUrl, type: 'image' as const, thumbnailUrl: '' } })
                              };
                              await saveDraft(draftPayload);
                            }
                            // Redirect to My Content Hub after successful save
                            navigate('/my-content-hub');
                          } finally {
                            setIsSavingDrafts(false);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Save className="h-4 w-4 mr-1.5" />
                        {t('contentCreation.results.saveAll', 'Save all')}
                      </button>
                    </div>
                  )}
                </div>

                {!contentGeneration.isPending && (
                  <>
                    {generatedContent?.data?.contents && generatedContent.data.contents.length > 0 ? (
                      <div className="grid grid-cols-1 gap-6 items-start">
                        {/* Generated image preview removed per UX request */}
                        <div className="space-y-6">
                          {generatedContent.data.contents.map((item, index) => (
                            <ContentResultCard
                              key={item.platform || index}
                              platform={item.platform}
                              content={item.content}
                              hashtags={item.hashtags}
                              onCreatePost={(platform, content, hashtags) => handleCreatePost(platform, content, hashtags)}
                              brandId={formData.brandId}
                              format={formData.format}
                              imageUrl={generatedContent.imageUrl}
                            />
                          ))}
                        </div>
                      </div>
                    ) : generatedContent?.content?.platforms ? (
                      // Fallback for direct rendering of new format if transformation didn't work
                      <div className="grid grid-cols-1 gap-6 items-start">
                        {/* Generated image preview removed per UX request */}
                        <div className="space-y-6">
                          {Object.entries(generatedContent.content.platforms).map(([platform, content], index) => (
                            <ContentResultCard
                              key={platform}
                              platform={platform.toLowerCase()}
                              content={content}
                              hashtags={extractHashtags(typeof content === 'string' ? content : content.text || '')}
                              onCreatePost={(platform, content, hashtags) => handleCreatePost(platform, content, hashtags)}
                              brandId={formData.brandId}
                              format={formData.format}
                              imageUrl={generatedContent.imageUrl}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64 text-center">
                        <p className="text-gray-500 max-w-md">
                          {t('contentCreation.results.noContent')}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
    </PageLayout>

      {/* Content Generation Loader */}
      <ContentGenerationLoader 
        isVisible={contentGeneration.isPending} 
        hasImage={Boolean(formData.generateImage)}
      />

      {/* AI Post Draft Modal */}
      {generatedContent?.data?.contents && selectedPostForEditing && (
        <AIPostDraftModal
          isOpen={isAIPostDraftModalOpen}
          onClose={() => {
            setIsAIPostDraftModalOpen(false);
            setSelectedPostForEditing(null);
          }}
          onDiscard={handleDiscardOtherPosts}
          onSaveAndProceed={handleSaveOtherPostsAndProceed}
          totalPosts={generatedContent.data.contents.length}
          isLoadingSave={isSavingDrafts}
        />
      )}

      {/* Waiting Modal for saving drafts */}
      <WaitingModal
        isOpen={isSavingDrafts}
        title={t('contentCreation.modals.savingDrafts.title', 'Saving Drafts')}
        message={t('contentCreation.modals.savingDrafts.message', 'Please wait while we save the other generated posts as drafts...')}
        videoSrc="/Panda Riding Horse for GIF.mov"
        gifUrl="https://media.socialness.ai/loading/unicorn.gif"
      />

      {/* Image Regeneration Modal */}
      <AISimpleGenerationModal
        isOpen={isImageRegenerationModalOpen}
        onClose={() => {
          setIsImageRegenerationModalOpen(false);
          setTemporaryGeneratedImageUrl(null);
        }}
        onGenerate={handleImageRegenerate}
        onValidate={handleImageValidation}
        isLoading={imageRegeneration.isPending}
        currentImageStyle={(lastGeneratedImageStyle || formData.imageStyle || 'auto') as string}
        currentImageSize={(lastGeneratedImageSize || (formData.imageSize as ImageSize) || '4:5') as ImageSize}
        generatedImageUrl={temporaryGeneratedImageUrl || generatedContent?.imageUrl || null}
      />

      {/* Text Compare Modal */}
      <AITextCompareModal
        isOpen={isCompareModalOpen}
        items={compareItems}
        onClose={() => setIsCompareModalOpen(false)}
        onApply={(selection) => {
          if (!generatedContent) {
            setIsCompareModalOpen(false);
            return;
          }
          // Apply selection to current generatedContent without touching image
          const updatedContents = (generatedContent.data?.contents || []).map((c: any) => {
            const platform = (c.platform || 'unknown').toLowerCase();
            const choice = selection[platform];
            if (choice === 'new' && pendingNewTextByPlatform[platform]) {
              const newText = pendingNewTextByPlatform[platform];
              let newContent: any = newText;
              if (c && typeof c.content === 'object' && c.content !== null && 'text' in c.content) {
                newContent = { ...c.content, text: newText };
              }
              return { ...c, content: newContent };
            }
            return c; // keep old
          });

          const updated: ContentGenerationResponse = {
            ...(generatedContent as any),
            data: {
              ...(generatedContent.data as any),
              contents: updatedContents,
            },
          };
          setGeneratedContent(updated);
          setIsCompareModalOpen(false);
          setPendingNewTextByPlatform({});
        }}
      />
    </>
  );
};

// Wrap ContentCreation with DatabaseErrorBoundary to handle IndexedDB issues
const ContentCreationWithErrorBoundary = () => (
  <DatabaseErrorBoundary>
    <ContentCreation />
  </DatabaseErrorBoundary>
);

export default ContentCreationWithErrorBoundary;
